// ══════════════════════════════════════════════════════════════════════════════
// CALL MODAL - Appels vocaux et vidéo pour AbracadaTchat
// Fonctionnalités: Appel audio, appel vidéo, mute, camera toggle, raccrocher
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Phone, PhoneOff, Video, VideoOff, Mic, MicOff, 
  User, Users, X, Volume2, VolumeX, Maximize2, Minimize2
} from 'lucide-react';

const CallModal = ({ 
  isOpen, 
  onClose, 
  callType, // 'audio' | 'video'
  conversation,
  isIncoming = false,
  callerName = '',
  onAccept,
  onReject
}) => {
  const [callState, setCallState] = useState('calling'); // 'calling' | 'ringing' | 'connected' | 'ended'
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const localStreamRef = useRef(null);
  const callTimerRef = useRef(null);
  const ringtoneRef = useRef(null);

  // Nom du participant
  const participantName = conversation?.type === 'group' 
    ? conversation.group_name 
    : conversation?.participants?.map(p => p.display_name).join(', ') || callerName || 'Utilisateur';

  // Démarrer/arrêter la sonnerie
  useEffect(() => {
    if (isOpen && (callState === 'calling' || callState === 'ringing')) {
      // Jouer la sonnerie (simulée pour l'instant)
      const playRingtone = () => {
        // On pourrait ajouter un vrai son ici
        console.log('🔔 Sonnerie...');
      };
      const interval = setInterval(playRingtone, 2000);
      ringtoneRef.current = interval;
      
      return () => {
        if (ringtoneRef.current) {
          clearInterval(ringtoneRef.current);
        }
      };
    }
  }, [isOpen, callState]);

  // Initialiser le média local
  useEffect(() => {
    if (isOpen && !isIncoming) {
      initializeMedia();
    }
    
    return () => {
      cleanupMedia();
    };
  }, [isOpen, callType]);

  // Timer de durée d'appel
  useEffect(() => {
    if (callState === 'connected') {
      callTimerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    }
    
    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
    };
  }, [callState]);

  // Simuler la connexion après 2 secondes (en prod: WebRTC signaling)
  useEffect(() => {
    if (isOpen && callState === 'calling' && !isIncoming) {
      const timeout = setTimeout(() => {
        // Simuler que l'autre personne répond
        setCallState('connected');
      }, 2500);
      
      return () => clearTimeout(timeout);
    }
  }, [isOpen, callState, isIncoming]);

  const initializeMedia = async () => {
    try {
      const constraints = {
        audio: true,
        video: callType === 'video'
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      localStreamRef.current = stream;
      
      if (localVideoRef.current && callType === 'video') {
        localVideoRef.current.srcObject = stream;
      }
      
      setCallState('calling');
    } catch (error) {
      console.error('Erreur accès média:', error);
      // Continuer en mode simulation même sans accès micro/caméra
      console.log('Mode simulation activé (pas d\'accès média)');
      setCallState('calling');
    }
  };

  const cleanupMedia = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
    }
    if (ringtoneRef.current) {
      clearInterval(ringtoneRef.current);
    }
    setCallDuration(0);
    setCallState('calling');
  };

  const handleEndCall = () => {
    cleanupMedia();
    setCallState('ended');
    setTimeout(() => {
      onClose();
    }, 500);
  };

  const handleAcceptCall = async () => {
    await initializeMedia();
    setCallState('connected');
    onAccept?.();
  };

  const handleRejectCall = () => {
    onReject?.();
    onClose();
  };

  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  const toggleCamera = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsCameraOff(!videoTrack.enabled);
      }
    }
  };

  const toggleSpeaker = () => {
    setIsSpeakerOn(!isSpeakerOn);
    // En prod: changer la sortie audio
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10010] bg-black/90 flex items-center justify-center"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className={`relative bg-gradient-to-b from-slate-800 to-slate-900 rounded-3xl overflow-hidden shadow-2xl ${
            isFullscreen ? 'w-full h-full rounded-none' : 'w-full max-w-md mx-4'
          }`}
        >
          {/* Header avec bouton fermer */}
          <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5 text-white" /> : <Maximize2 className="w-5 h-5 text-white" />}
            </button>
          </div>

          {/* Zone vidéo principale */}
          {callType === 'video' && callState === 'connected' && (
            <div className="relative w-full aspect-[3/4] bg-slate-900">
              {/* Vidéo distante (simulée avec avatar pour l'instant) */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
                  {conversation?.type === 'group' ? (
                    <Users className="w-16 h-16 text-white" />
                  ) : (
                    <span className="text-5xl font-bold text-white">
                      {participantName.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Vidéo locale (petit encart) */}
              <div className="absolute bottom-4 right-4 w-28 h-36 rounded-xl overflow-hidden bg-slate-800 border-2 border-white/20">
                <video
                  ref={localVideoRef}
                  autoPlay
                  playsInline
                  muted
                  className={`w-full h-full object-cover ${isCameraOff ? 'hidden' : ''}`}
                />
                {isCameraOff && (
                  <div className="w-full h-full flex items-center justify-center">
                    <VideoOff className="w-8 h-8 text-gray-400" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Zone audio (pas de vidéo) ou en attente */}
          {(callType === 'audio' || callState !== 'connected') && (
            <div className="py-16 px-8 flex flex-col items-center">
              {/* Avatar */}
              <motion.div 
                animate={callState === 'calling' || callState === 'ringing' ? { scale: [1, 1.1, 1] } : {}}
                transition={{ repeat: Infinity, duration: 1.5 }}
                className="relative mb-6"
              >
                <div className="w-28 h-28 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/30">
                  {conversation?.type === 'group' ? (
                    <Users className="w-14 h-14 text-white" />
                  ) : conversation?.participants?.[0]?.avatar ? (
                    <img 
                      src={conversation.participants[0].avatar} 
                      alt="" 
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <span className="text-4xl font-bold text-white">
                      {participantName.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                
                {/* Indicateur d'appel */}
                {(callState === 'calling' || callState === 'ringing') && (
                  <motion.div
                    animate={{ scale: [1, 1.5], opacity: [0.5, 0] }}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                    className="absolute inset-0 rounded-full border-4 border-orange-500"
                  />
                )}
              </motion.div>

              {/* Nom */}
              <h2 className="text-2xl font-bold text-white mb-2">
                {participantName}
              </h2>

              {/* Statut */}
              <p className="text-gray-400 mb-8">
                {callState === 'calling' && 'Appel en cours...'}
                {callState === 'ringing' && 'Sonnerie...'}
                {callState === 'connected' && (
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    {formatDuration(callDuration)}
                  </span>
                )}
                {callState === 'ended' && 'Appel terminé'}
              </p>

              {/* Type d'appel */}
              <div className="flex items-center gap-2 mb-8 px-4 py-2 rounded-full bg-white/10">
                {callType === 'video' ? (
                  <>
                    <Video className="w-4 h-4 text-orange-400" />
                    <span className="text-sm text-gray-300">Appel vidéo</span>
                  </>
                ) : (
                  <>
                    <Phone className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-gray-300">Appel vocal</span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Contrôles */}
          <div className="px-8 pb-8">
            {/* Appel entrant - Accepter/Refuser */}
            {isIncoming && callState === 'ringing' ? (
              <div className="flex items-center justify-center gap-8">
                <button
                  onClick={handleRejectCall}
                  className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors shadow-lg shadow-red-500/30"
                >
                  <PhoneOff className="w-7 h-7 text-white" />
                </button>
                <button
                  onClick={handleAcceptCall}
                  className="w-16 h-16 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center transition-colors shadow-lg shadow-green-500/30"
                >
                  {callType === 'video' ? (
                    <Video className="w-7 h-7 text-white" />
                  ) : (
                    <Phone className="w-7 h-7 text-white" />
                  )}
                </button>
              </div>
            ) : (
              /* Contrôles pendant l'appel */
              <div className="flex items-center justify-center gap-4">
                {/* Mute */}
                <button
                  onClick={toggleMute}
                  className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                    isMuted ? 'bg-red-500/20 text-red-400' : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
                </button>

                {/* Camera (vidéo only) */}
                {callType === 'video' && (
                  <button
                    onClick={toggleCamera}
                    className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                      isCameraOff ? 'bg-red-500/20 text-red-400' : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    {isCameraOff ? <VideoOff className="w-6 h-6" /> : <Video className="w-6 h-6" />}
                  </button>
                )}

                {/* Raccrocher */}
                <button
                  onClick={handleEndCall}
                  className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors shadow-lg shadow-red-500/30"
                >
                  <PhoneOff className="w-7 h-7 text-white" />
                </button>

                {/* Haut-parleur */}
                <button
                  onClick={toggleSpeaker}
                  className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                    !isSpeakerOn ? 'bg-red-500/20 text-red-400' : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  {isSpeakerOn ? <Volume2 className="w-6 h-6" /> : <VolumeX className="w-6 h-6" />}
                </button>
              </div>
            )}

            {/* Hint */}
            {callState === 'connected' && (
              <p className="text-center text-gray-500 text-xs mt-6">
                Appuyez sur le bouton rouge pour raccrocher
              </p>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CallModal;
